//MainMap.ts =================================================
import "./Util/Prototypes";

import * as BasemapGallery from "./Widgets/BasemapGallery";
import * as CompareMaps from "./Widgets/CompareMaps";
import * as Custom from "./Custom";
import * as DrawerMenu from "./Menus/DrawerMenu";
import * as ElevationProfile from "./Widgets/ElevationProfile";
import * as FeatureLayerLabels from "./Util/FeatureLayerLabels";
import * as GraLay from "./Util/GraLay";
import * as Highlight from "./Util/Highlight";
import * as HighlightGraphics from "./Util/HighlightGraphics";
import * as Legend from "./Widgets/Legend";
import * as MainMap from "./MainMap";
import * as MainToolbar from "./Menus/MainToolbar";
import * as MapLayers from "./Widgets/MapLayers";
import * as MapThemes from "./Widgets/MapThemes";
import * as MapTips from "./Util/MapTips";
import * as PrevNextExtent from "./Menus/PrevNextExtent";
import * as QueryString from "./QueryString";
import * as ScaleDependentRender from "./Util/ScaleDependentRender";
import * as SelectOnMap from "./Search/SelectOnMap";
import * as Settings from "./Settings";
import * as Spinner from "./Util/Spinner";
import * as Util from "./Util/Util";
import * as Util_App from "./Util/Util_App";
import * as Widgets from "./Widgets/Widgets";

//ESRI javascript API.
import Basemap from "@arcgis/core/Basemap";
import ElevationLayer from "@arcgis/core/layers/ElevationLayer";
import esriConfig from "@arcgis/core/config";
import Extent from "@arcgis/core/geometry/Extent";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GeoRSSLayer from "@arcgis/core/layers/GeoRSSLayer";
import Ground from "@arcgis/core/Ground";
import GroupLayer from "@arcgis/core/layers/GroupLayer";
import ImageryLayer from "@arcgis/core/layers/ImageryLayer";
import ImageryTileLayer from "@arcgis/core/layers/ImageryTileLayer";
//import IntegratedMeshLayer from "@arcgis/core/layers/IntegratedMeshLayer";
import KMLLayer from "@arcgis/core/layers/KMLLayer";
import Map from "@arcgis/core/Map";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import MapView from "@arcgis/core/views/MapView";
//import PointCloudLayer from "@arcgis/core/layers/PointCloudLayer";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
//import SceneLayer from "@arcgis/core/layers/SceneLayer";
import TileLayer from "@arcgis/core/layers/TileLayer";
import * as urlUtils from "@arcgis/core/core/urlUtils";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer";
import WMSLayer from "@arcgis/core/layers/WMSLayer";
import WMTSLayer from "@arcgis/core/layers/WMTSLayer";

type mapImageFormat = "png" | "png8" | "png24" | "png32" | "jpg" | "pdf" | "bmp" | "gif" | "pngjpg";
type imageryLayerFormat = "png" | "png8" | "png24" | "png32" | "jpg" | "bmp" | "gif" | "jpgpng" | "lerc" | "tiff";

export let map: __esri.Map;
export let mapView: __esri.MapView;
export let sceneView: __esri.SceneView;
export let activeView: __esri.MapView | __esri.SceneView;
export let view2D: boolean = true;

export let basemaps: __esri.Basemap[]; //Exported for use in base maps gallery.
export let startUpInProgress: boolean;
export let reBuildMapInProgress: boolean;

export let mapExtentFull: __esri.Extent;
let mapExtentStart: __esri.Extent;

let graphicsLayers: __esri.GraphicsLayer[];
let readyForRefresh2D: boolean = false;
const viewDiv1 = document.getElementById("viewDiv1") as HTMLDivElement;
const viewDiv2 = document.getElementById("viewDiv2") as HTMLDivElement;
export let activeViewDiv: HTMLDivElement = viewDiv1;
let oldViewDiv: HTMLDivElement = viewDiv2;
let startupBasemap: __esri.Basemap;
let mapTheme: Settings.iMapTheme;
let busy: boolean = true;

//Build all esri components.
export function build() {

   startUpInProgress = true;
   initMapExtents();

   //No 'Access-Control-Allow-Origin' header is present on the requested resource.
   //Origin 'http://localhost:65218' is therefore not allowed access. The response had HTTP status code 500.

   Util.logMsg("Setting proxy URL, portal URL's and CORS enabled servers.");

   //Portal URL.  Not used in browser.  Only used server-side.
   //if (Settings.portalUrl && !Settings.portalUrl.Contains("tiles.arcgis.com")) {
   //   esriConfig.portalUrl = Settings.portalUrl;
   //}

   //Proxy URL and rules.
   if (Settings.proxyUrl && Settings.proxyMapServiceURLs.length > 0) {
      esriConfig.request.proxyUrl = Settings.proxyUrl;

      for (const proxyRule of Settings.proxyMapServiceURLs) {
         urlUtils.addProxyRule({ proxyUrl: Settings.proxyUrl, urlPrefix: proxyRule });
      }
   }

   //CORS enabled servers.
   for (const corServer of Settings.corsEnabledServers) {
      esriConfig.request.trustedServers.push(corServer);
   }

   buildMap();
}

//Build only the map components on startup and when 2D/3D toggle.
function buildMap(zoomGeom?: __esri.Extent | __esri.Viewpoint) {
   reBuildMapInProgress = true;

   //Don't show tools that only work in 2D.
   if (zoomGeom) {
      const ok: boolean = DrawerMenu.isValidTool3D(DrawerMenu.prevToolName);
      if (!ok) {
         DrawerMenu.initTool("Quick Search");
      }
   }

   //Get startup map theme setting.
   mapTheme = MapThemes.getMapTheme(MapThemes.activeMapTheme);

   if (!MainMap.activeView) { //Keep previous map theme when 2D/3D toggle.
      if (QueryString.mapTheme) {
         mapTheme = MapThemes.getMapTheme(QueryString.mapTheme);
         QueryString.setMapTheme(null);

      } else if (localStorage.Exists(MapLayers.themeName)) {
         mapTheme = localStorage.GetItem(MapLayers.themeName) as Settings.iMapTheme;
      }
   }

   //Load map services.
   if (!basemaps) {
      basemaps = loadBasemaps(); // Required for basemap gallery widget.
   }

   const operationalLayers = loadOperationalLayers(); //Reload to filter out slow layers in 3D.

   if (!graphicsLayers) {
      graphicsLayers = GraLay.loadGraphicsLayers();
   }

   //Create map object.
   Util.logMsg('Creating Map');

   //Set startup basemap.
   if (MainMap.activeView) {
      startupBasemap = MainMap.map.basemap; //Keep previous basemap when 2D/3D toggle.

   } else {
      //Set startup basemap.
      startupBasemap = basemaps[basemaps.length - 1];

      for (const bm of basemaps) {
         const bmFound: boolean = MapThemes.mapServiceExistInMapTheme(mapTheme, bm.id);
         if (bmFound) {
            startupBasemap = bm;
            break;
         }
      }
   }

   //Create main map.
   map = new Map({
      basemap: startupBasemap,
      layers: operationalLayers,
   });

   //Set the ground terrain.
   MainMap.map.ground = loadGround() as __esri.Ground;

   //Create map or scene view.
   if (MainMap.view2D) {
      createMapView(zoomGeom as __esri.Extent);
   } else {
      createSceneView(zoomGeom as __esri.Viewpoint);
   }

   //Set flags to reload items after creating map view or scene view.
   CompareMaps.setReloadBasemaps(true);
   SelectOnMap.setCreateSketchVM(true);

   //Initialize prev next buttons.
   if (startUpInProgress) {
      PrevNextExtent.render();
   }
}

//Load base maps.
function loadBasemaps(): __esri.Basemap[] {
   const basemaps2: __esri.Basemap[] = [];
   let basemap: __esri.Basemap;
   let baseLayer: __esri.Layer;
   let props: __esri.FeatureLayerProperties;
   let opacity: number;
   let legendLayer: Settings.iLegendLayer;

   for (const bm of Settings.basemaps) {

      opacity = MapThemes.getMapThemeOpacity(mapTheme, bm.id); //Check startup map theme for opacity.
      if (opacity === -1) {
         opacity = null;
         if (bm.opacity !== 1) {
            opacity = bm.opacity; //Use opacity from the settings.
         }
      }

      props = {
         id: bm.id,
         opacity: opacity,
         url: bm.mapServiceURL,
         visible: true,
         legendEnabled: true,
      };

      //Hide in legend if setting found.
      if (!bm.serviceType.Equal("VectorTileLayer")) {
         legendLayer = Legend.getLegendLayerSetting(bm.id);
         if (legendLayer) {
            props.legendEnabled = false;
         }
      }

      //Process all map service types.
      if (bm.serviceType.Equal("TileLayer")) {
         const tileLayerProps = props as __esri.TileLayerProperties;
         baseLayer = new TileLayer(tileLayerProps);

      } else if (bm.serviceType.Equal("MapImageLayer")) {
         const mapImageLayerProps = props as __esri.MapImageLayerProperties;

         if (bm.imageFormat) {
            mapImageLayerProps.imageFormat = bm.imageFormat as mapImageFormat;
         }
         baseLayer = new MapImageLayer(mapImageLayerProps);

      } else if (bm.serviceType.Equal("ImageryLayer")) {
         const imageryLayerProps = props as __esri.ImageryLayerProperties;

         if (bm.imageFormat) {
            imageryLayerProps.format = bm.imageFormat as imageryLayerFormat;
         }
         baseLayer = new ImageryLayer(imageryLayerProps);

      } else if (bm.serviceType.Equal("ImageryTileLayer")) {
         const imageryTileLayerProps = props as __esri.ImageryTileLayerProperties;
         baseLayer = new ImageryTileLayer(imageryTileLayerProps);

      } else if (bm.serviceType.Equal("VectorTileLayer")) {
         const vectorTileLayerProps = props as __esri.VectorTileLayerProperties;
         baseLayer = new VectorTileLayer(vectorTileLayerProps);

      } else if (bm.serviceType.Equal("WMSLayer")) {
         const wmsLayerProps = props as __esri.WMSLayerProperties;
         baseLayer = new WMSLayer(wmsLayerProps);

      } else if (bm.serviceType.Equal("WMTSLayer")) {
         const wmtsLayerProps = props as __esri.WMTSLayerProperties;
         baseLayer = new WMTSLayer(wmtsLayerProps);
      }

      //Set custom layer properties.
      Custom.setLayerProps(baseLayer);

      basemap = new Basemap({
         id: bm.id,
         title: bm.id,
         baseLayers: [baseLayer],
         thumbnailUrl: bm.thumbnail,
      });

      Util.logMsg('Adding basemap layer: "' + basemap.id + '"');
      basemaps2.Add(basemap);

      //Set spatial reference and build sub layers.
      baseLayer.when(function (layer: __esri.Layer) {
         Util.logMsg('Basemap layer is ready: "' + layer.id + '"');

         //Add spatial reference to basemaps array.
         for (const basemap3 of basemaps2) {
            if (basemap3.id === layer.id) {
               if (layer.type !== "wmts") { // wmts causes issues with Basemap Gallery.
                  const mapImageLayer = layer as __esri.MapImageLayer; //Hacky.
                  basemap3.spatialReference = mapImageLayer.spatialReference;
               }
            }
         }

         //Process sub layers in map image service.
         if (layer.type === "map-image") {
            const mapImageLayer = layer as __esri.MapImageLayer;

            if (mapImageLayer.sublayers && mapImageLayer.sublayers.length > 0) {
               Util_App.setSublayerIndexesInSettings(mapImageLayer); //Set sublayer id's in settings.
            }

         } else if (layer.type === "vector-tile") {
            Custom.setVectorTileLayerProps(layer as __esri.VectorTileLayer);
         }

      }).catch(function (error: Error) { //Trap for errors.
         Util.errMsg("Basemap layer failed to load.", error);
      });
   }

   return basemaps2;
}

//Load operational layers.
function loadOperationalLayers(): __esri.Layer[] {
   const operLayers: __esri.Layer[] = [];
   let operLayer: __esri.Layer;
   let props: __esri.FeatureLayerProperties;
   let subLayersOn: Settings.iSublayer[];
   let visible: boolean;
   let opacity: number;
   let legendLayer: Settings.iLegendLayer;

   let groupLayerInProgress: boolean = false;
   let groupLay: __esri.GroupLayer;
   let codeAndName: Settings.iCodeName[];

   Util.logCurrentTime("*** Initializing all operational layers...");

   //Process all operational maps.
   for (const om of Settings.operMaps) {

      if (MainMap.view2D) {
         //Skip 3D only layer types for 2D mode.
         if (om.serviceType.Equal("SceneLayer") || om.serviceType.Equal("IntegratedMeshLayer") || om.serviceType.Equal("PointCloudLayer")) {
            continue;
         }
         visible = MapThemes.mapServiceExistInMapTheme(mapTheme, om.id); //Check startup map theme for visibility.

      } else { //Skip layers that take too long to display in 3D.
         //if (!Util_App.isMapLayerVisible(om.id, true)) {
         //   continue;
         //}
         if (!Settings.layerIDsFor3D.Contains(om.id) && om.serviceType !== "GroupLayer") {
            continue;
         }
         visible = Util_App.isMapLayerVisible(om.id, true);
      }

      opacity = MapThemes.getMapThemeOpacity(mapTheme, om.id); //Check startup map theme for opacity.
      if (opacity === -1) {
         opacity = null;
         if (om.opacity !== 1) {
            opacity = om.opacity; //Use opacity from the settings.
         }
      }

      if (om.serviceType === "GroupLayer") {
         if (om.mapServiceName === "End") { //Start a group layer.
            groupLayerInProgress = true;
            groupLay = new GroupLayer({ id: om.id, title: om.id, visible: visible });

         } else { //Finish a group layer.
            groupLayerInProgress = false;
            Util.logMsg('Adding group layer: "' + groupLay.title + '"');
            operLayers.Add(groupLay);
         }

      } else {
         props = {
            id: om.id,
            title: om.id,
            opacity: opacity,
            url: om.mapServiceURL,
            visible: visible,
         };

         if (om.serviceType.Equal("MapImageLayer")) {
            const mapImageLayerProps = props as __esri.MapImageLayerProperties;

            if (om.id.toLowerCase().endsWith("query")) { //To hide query only map image services.
               mapImageLayerProps.visible = true;
               mapImageLayerProps.opacity = 0;
               mapImageLayerProps.legendEnabled = false;

               if (Settings.hideSearchAndSelectLayersInTOC) {
                  mapImageLayerProps.listMode = "hide";
               }
            }

            if (om.imageFormat) {
               mapImageLayerProps.imageFormat = om.imageFormat as mapImageFormat;
            }
            operLayer = new MapImageLayer(mapImageLayerProps);

         } else if (om.serviceType.Equal("FeatureLayer")) {
            const featureLayerProps = props;

            if (om.id.toLowerCase().startsWith("_")) { //For feature layers.
               featureLayerProps.opacity = 0;
               featureLayerProps.legendEnabled = false;

               if (Settings.hideSearchAndSelectLayersInTOC) {
                  featureLayerProps.listMode = "hide";
               }
            }

            legendLayer = Legend.getLegendLayerSetting(om.id);
            if (legendLayer) {
               featureLayerProps.legendEnabled = false; //Hide in legend if layer name found.
            }
            if (om.mapServiceURL.EndsWith("FeatureServer")) {
               Util.errMsg("Feature layer not found in REST endpoint: " + om.id);
               continue; //Feature layer index was not found in feature service REST endpoint.
            }
            operLayer = new FeatureLayer(featureLayerProps);

         } else if (om.serviceType.Equal("VectorTileLayer")) {
            const vectorTileLayerProps = props as __esri.VectorTileLayerProperties;
            operLayer = new VectorTileLayer(vectorTileLayerProps);

         } else if (om.serviceType.Equal("TileLayer")) {
            const tileLayerProps = props as __esri.TileLayerProperties;
            operLayer = new TileLayer(tileLayerProps);

         } else if (om.serviceType.Equal("GeoRSSLayer")) {
            const geoRSSLayerProps = props as __esri.GeoRSSLayerProperties;
            operLayer = new GeoRSSLayer(geoRSSLayerProps);

         } else if (om.serviceType.Equal("KMLLayer")) {
            const kmlLayerProps = props as __esri.KMLLayerProperties;
            operLayer = new KMLLayer(kmlLayerProps);

            //} else if (om.serviceType.Equal("SceneLayer")) {
            //   const sceneLayerProps = props as __esri.SceneLayerProperties;
            //   operLayer = new SceneLayer(sceneLayerProps);

            //} else if (om.serviceType.Equal("IntegratedMeshLayer")) {
            //   const integratedMeshLayerProps = props as __esri.IntegratedMeshLayerProperties;
            //   operLayer = new IntegratedMeshLayer(integratedMeshLayerProps);

            //} else if (om.serviceType.Equal("PointCloudLayer")) {
            //   const pointCloudLayerProps = props as __esri.PointCloudLayerProperties;
            //   delete pointCloudLayerProps.opacity;
            //   operLayer = new PointCloudLayer(pointCloudLayerProps);

         } else if (om.serviceType.Equal("WMSLayer")) {
            const wmsLayerProps = props as __esri.WMSLayerProperties;
            operLayer = new WMSLayer(wmsLayerProps);

         } else if (om.serviceType.Equal("WMTSLayer")) {
            const wmtsLayerProps = props as __esri.WMTSLayerProperties;
            operLayer = new WMTSLayer(wmtsLayerProps);
         }

         //Set custom layer properties.
         Custom.setLayerProps(operLayer);

         //Operational layer is ready but not loaded.
         operLayer.when(function (layer: __esri.Layer) {
            Util.logCurrentTime('*** Operational layer is ready: "' + layer.id + '"');

            //Process feature layer.
            if (layer.type === "feature") {
               const featureLayer = layer as __esri.FeatureLayer;

               //Report editing security risk.
               if (featureLayer.capabilities.operations.supportsEditing) {
                  Util.errMsg("Feature layer supports editing!: " + layer.id);
               }

               // Load coded value domains for feature layers.  Subtypes are only available at the REST endpoint.
               if (featureLayer.fields) {

                  for (const field of featureLayer.fields) {
                     if (field.domain) {
                        const domain = field.domain as __esri.CodedValueDomain;

                        if (domain.codedValues && domain.codedValues.length > 0) {
                           //   if (typeof field.domain.codedValues[0].code === "number") {
                           const codedValue: Settings.iCodedValue = { layerName: layer.id, fieldName: field.name, values: [] };

                           for (const value of domain.codedValues) {
                              codedValue.values.Add({ code: value.code, name: value.name });
                           }
                           Settings.codedValues.Add(codedValue);
                        }
                     }
                  }
               }

               //Process map image service.
            } else if (layer.type === "map-image" || layer.type === "tile") {
               const mapImageLayer = layer as __esri.MapImageLayer;

               mapImageLayer.loadAll().then(function () {
                  if (mapImageLayer.sublayers && mapImageLayer.sublayers.length > 0) {

                     //Set sublayer id's in settings.
                     Util_App.setSublayerIndexesInSettings(mapImageLayer);

                     //Turn on sublayers in the default map theme.
                     if (layer.type !== "tile") {
                        subLayersOn = MapThemes.getMapThemeVisibleSublayers(mapTheme, layer.id);
                        MapThemes.setSublayerVisibility(mapImageLayer.sublayers, subLayersOn);
                     }

                     //Turn off map image service sublayers in legend.
                     legendLayer = Legend.getLegendLayerSetting(layer.id);
                     if (legendLayer) {
                        Legend.hideLegendSublayers(mapImageLayer.sublayers, legendLayer.sublayerNames);
                     }

                     // Load coded value domains and subtypes for map image services.
                     for (const sublayer of mapImageLayer.allSublayers) {

                        // Load coded value domains for map image services.
                        for (const codedValueSpec of Settings.codedValueSpecs) {
                           if (codedValueSpec.layerName === mapImageLayer.id) {

                              for (const sublayerName of codedValueSpec.sublayerNames) {
                                 if (sublayerName === sublayer.title) {

                                    for (const sublayerField of sublayer.fields) {
                                       if (sublayerField.domain && sublayerField.domain.type === "coded-value") {
                                          if (sublayerField.domain.codedValues.length > 0) {
                                             codeAndName = [];

                                             for (const codedValue of sublayerField.domain.codedValues) {
                                                codeAndName.Add({ code: codedValue.code, name: codedValue.name });
                                             }
                                             Settings.codedValues.Add({ layerName: sublayerName, fieldName: sublayerField.name, values: codeAndName });
                                          }
                                       }
                                    }
                                 }
                              }
                           }
                        }

                        // Load subtypes for map image services.
                        if (sublayer.typeIdField && sublayer.types && sublayer.types.length > 0) {
                           //Util.errMsg("sublayer title: " + sublayer.title + " " + sublayer.typeIdField, sublayer);
                           codeAndName = [];

                           for (const sublayerType of sublayer.types) {
                              codeAndName.Add({ code: sublayerType.id, name: sublayerType.name });
                           }
                           Settings.codedValues.Add({ layerName: sublayer.title, fieldName: sublayer.typeIdField, values: codeAndName });
                        }
                     } // End process all sublayers.
                  }
               });

            } else if (layer.type === "vector-tile") {
               Custom.setVectorTileLayerProps(layer as __esri.VectorTileLayer);
            }

         }).catch(function (error: Error) { //Trap for errors.
            Util.errMsg("Operational layer failed to load.", error);
         });

         Util.logMsg('Adding operational layer: "' + operLayer.id + '"');
         if (groupLayerInProgress) {
            groupLay.layers.add(operLayer);
         } else {
            operLayers.Add(operLayer);
         }

      } //end if group layer or not.
   } //end for all operational map settings.

   //return myGroupLayer;
   return operLayers;
}

//Load ground elevation.
function loadGround(): string | __esri.Ground {
   const elevationLayers: __esri.ElevationLayer[] = [];
   let elevationLayer: __esri.ElevationLayer;
   let validLayer: boolean;
   let isWebMercator: boolean = Util_App.isWebMercator();

   for (const em of Settings.elevMaps) {
      validLayer = false;

      if (MainMap.view2D) {
         if (em.id.toLowerCase().Contains("profile")) {
            validLayer = true;
         }

      } else { // 3D.
         if (!isWebMercator && !em.isGlobal || isWebMercator && em.isGlobal) {
            validLayer = true;
         }
      }

      if (validLayer) {
         elevationLayer = new ElevationLayer({
            id: em.id,
            url: em.mapServiceURL,
            visible: true, //Required to be true.
         });

         //Trap for errors.
         elevationLayer.when().catch(function (error: Error) {
            Util.errMsg("Elevation layer failed to load.", error);
         });

         Util.logMsg('Adding elevation layer: "' + elevationLayer.id + '"');
         elevationLayers.Add(elevationLayer);
      }
   }

   let ground: string | __esri.Ground = "world-elevation";

   if (elevationLayers.length > 0) {
      ground = new Ground({ layers: elevationLayers, navigationConstraint: { type: "stay-above" } }); // "stay-above" or "none"
   }
   return ground;
}

//Create 2D map view.
function createMapView(mapExtent: __esri.Extent) {

   Util.logMsg('Creating map view.');

   //const webGL_supported = document.createElement('canvas').getContext('webgl'); //This does not work.
   const webGL2_supported = document.createElement('canvas').getContext('webgl2');

   if (!webGL2_supported) {
      Util.errMsg("Your browser does not support version 2 of the Web Graphics Library (WebGL2). Please use a newer computer for this application.");
      //alert("");
      //return;
   }

   //Compute map extent.
   let extent = mapExtentStart;
   if (mapExtent) {
      extent = mapExtent; //When toggling to 2D, use the 3D map extent from the calling argument.
   }

   //Create map view.
   MainMap.activeViewDiv.innerHTML = ""; //Clear the map container.

   mapView = new MapView({
      container: MainMap.activeViewDiv,
      map: MainMap.map,
      constraints: { rotationEnabled: true, snapToZoom: true },
      highlightOptions: {
         color: Settings.hoverPolygonSym.color,
         haloColor: Settings.hoverPolygonSym.outline.color,
      },
      background: { color: "white" },
      extent: extent,
   });

   activeView = MainMap.mapView;

   //Remove initial attribution so it can be added as a widget.
   MainMap.mapView.ui.remove("attribution");

   //Map view is ready.
   MainMap.mapView.when(function () {
      Util.logMsg("Map view is ready");
      readyForRefresh2D = true;
      MainMap.map.ground.load(); //Force the ground elevation to load since it does not for some reason.

      //Zoom to start extent.
      Util.logMsg('Zooming to 2D extent.');
      MainMap.mapView.goTo(extent, Settings.animateOptions);

      //Add or remove 3D button when basemap changes.
      MainMap.mapView.watch("spatialReference", function (newspatialReference, oldspatialReference) {
         //Util.errMsg("MainMap -> Is Mercator: " + MainMap.mapView.spatialReference.isWebMercator);
         MainToolbar.addOrRemove3D();
         MainToolbar.render();
      });

      //Change the basemap by map scale.
      mapView.watch("scale", function () {

         if (Settings.basemapByScale.enabled && Settings.basemapByScale.active) {
            let basemap: __esri.Basemap;
            //Util.errMsg("Map scale changed to: " + MainMap.mapView.scale);

            if (mapView.scale < Settings.basemapByScale.mapScale) {
               if (map.basemap.id !== Settings.basemapByScale.zoomedInBasemapId) {
                  basemap = Util_App.getBasemapById(Settings.basemapByScale.zoomedInBasemapId);
               }

            } else {
               if (map.basemap.id !== startupBasemap.id) {
                  basemap = Util_App.getBasemapById(startupBasemap.id);
               }
            }
            if (basemap) {
               if (mapView.extent.intersects(Settings.basemapByScale.zoomedInBasemapExtent)) {
                  Settings.basemapByScale.inProgress = true;
                  Widgets.setBasemap(basemap);
                  setTimeout(function () { Settings.basemapByScale.inProgress = false; }, 2000);
               }
            }
         }
      });

      if (Settings.doubleBasemapTileLevels) {
         addNewLODs(); //Double the number of basemap tile levels of detail.
      }

      Highlight.createIdentifyTasks();
      HighlightGraphics.init();
      ScaleDependentRender.init();

      Widgets.createTrackWidget(MainMap.mapView);
      Widgets.createHome(MainMap.mapView);
      Widgets.createCompass(MainMap.mapView);
      Widgets.createBasemapToggle();
      Widgets.createCoordWidget();
      Widgets.createScalebar();

      PrevNextExtent.createPrevNextButtons();
      PrevNextExtent.init();

      Widgets.setPosition(); //Set the widget positions on website startup part 3.

   }).catch(function (error: Error) { //Trap for errors.
      Util.errMsg('Unable to create map view.', error);
   });

   //Refresh mapview after first map service is rendered.
   reactiveUtils.whenOnce(() => !MainMap.mapView.updating).then(() => {

      if (readyForRefresh2D && MainMap.mapView.ready) {
         Util.logMsg('Updating the mapView');
         readyForRefresh2D = false;

         toggleViewDivOpacity();
         if (MainMap.sceneView) {
            MainMap.sceneView.container = null;
         }

         //Add graphics layers to map view.
         MainMap.map.addMany(graphicsLayers);

         Widgets.setPosition(); //Set the widget positions on website startup part 2.

         if (DrawerMenu.classRef) {
            DrawerMenu.classRef.refresh();
         }
         if (MainToolbar) {
            MainToolbar.render();
         }

         reBuildMapInProgress = false;
         FeatureLayerLabels.init();
         MapTips.initMapTips();

         hideBusy();
      }

      Util.logMsg("Setting start Up In Progress to false.");
      startUpInProgress = false;
   });
} //end create map view.

//Create the scene view.
function createSceneView(viewPoint: __esri.Viewpoint) {
   import("@arcgis/core/views/SceneView").then(({ default: SceneView }) => {

      Util.logMsg('Creating scene view.');
      MainMap.activeViewDiv.innerHTML = ""; //Clear the map container.

      sceneView = new SceneView({
         container: MainMap.activeViewDiv,
         map: MainMap.map,
         viewingMode: "global", // local (flat) or global (spherical).
         //qualityMode: "medium",
         //environment: { atmosphere: null, starsEnabled: false },
         //clippingArea: mapExtent,
         highlightOptions: {
            color: Settings.hoverPolygonSym3D.color,
            haloColor: Settings.hoverPolygonSym3D.outline.color,
         },
         viewpoint: viewPoint,
      });

      activeView = MainMap.sceneView;

      //Remove initial attribution so it can be added as a widget.
      MainMap.sceneView.ui.remove("attribution");

      //Scene view is ready.
      MainMap.sceneView.when(function () {
         Util.logMsg("SceneView is ready.");

         Highlight.createIdentifyTasks();
         HighlightGraphics.init();

         Widgets.createTrackWidget(MainMap.sceneView);
         Widgets.createHome(MainMap.sceneView);
         Widgets.createBasemapToggle();
         Widgets.createCoordWidget();

      }).catch(function (error: Error) { //Trap for errors.
         Util.errMsg("SceneView failed to initialize.", error);
      });

      //Refresh the scene view after the first map service is rendered.
      reactiveUtils.whenOnce(() => !MainMap.sceneView.updating).then(() => {

         Util.logMsg('Updating sceneView is complete');

         if (MainMap.sceneView.ready) {

            toggleViewDivOpacity();
            MainMap.mapView.container = null;

            Util.logMsg('Tilting the sceneView');
            MainMap.sceneView.goTo({ tilt: 60 }, { duration: 800 });
            Widgets.setPosition();
            DrawerMenu.classRef.refresh();
            MainToolbar.render();

            //Add graphics layers to scene view.
            MainMap.map.addMany(graphicsLayers);

            reBuildMapInProgress = false;
            FeatureLayerLabels.init();
            MapTips.initMapTips();

            //Util.errMsg("3D Performance Info", MainMap.sceneView.performanceInfo);
            hideBusy();
         }
      });

      //Check for Web GL support.
      let webGL_supported = true;

      MainMap.sceneView.when().catch(function (error: Error) {
         webGL_supported = false;
         Util.errMsg('Unable to create scene view. WebGL may not be supported in this browser.', error);

         //Switch to 2D mode if 3D is not supported.
         view2D = true;
         MainMap.activeViewDiv.innerHTML = ""; //Clear the map container.
         setTimeout(function () { buildMap(); }, 1000);
      });

   }).catch((error: Error) => {
      Util.errMsg('An error occurred while loading the SceneView module:', error);
   });

} //end create scene view.

//Toggle between 2D and 3D.
export function toggle_2D_3D() {
   if (busy) {
      return;
   }

   showBusy();
   DrawerMenu.closeTool();

   // Destroy tools that never get unmounted.
   BasemapGallery.destroy();
   ElevationProfile.destroy();
   MapLayers.destroy();

   //Change to 3D view.
   if (MainMap.view2D) {
      Util.logMsg('Changing to 3D mode.');
      view2D = !MainMap.view2D; //Toggle the view type.
      Widgets.removeScalebar();
      const viewPoint = mapView.viewpoint.clone();

      //Work around scale bug.
      if (viewPoint.scale < 3000) {
         Util.logMsg('Setting viewPoint scale to: 3000');
         viewPoint.scale = 3000;
      }

      toggleViewDiv();
      buildMap(viewPoint); //Rebuild the map.

   } else { //Change to 2D view.
      Util.logMsg('Changing to 2D mode.');
      view2D = !MainMap.view2D; //Toggle the view type.

      MainMap.sceneView.goTo({ tilt: 0 }, { duration: 800 }).then(function () {
         toggleViewDiv();
         const mapExtent = MainMap.sceneView.extent.clone();
         buildMap(mapExtent); //Rebuild the map.
      });
   }
}

//Toggle the active view div.
function toggleViewDiv() {
   Util.logMsg('Toggling view div.');

   if (MainMap.activeViewDiv.id === viewDiv1.id) {
      activeViewDiv = viewDiv2;
      oldViewDiv = viewDiv1;
   } else {
      activeViewDiv = viewDiv1;
      oldViewDiv = viewDiv2;
   }
   Util.logMsg('Active container div set to: ' + MainMap.activeViewDiv.id);

   MainMap.activeViewDiv.innerHTML = ""; //Clear the map container.
   MainMap.activeViewDiv.style.opacity = ".01";
   MainMap.activeViewDiv.style.visibility = "visible";
}

//Toggle the active view div opacity.
function toggleViewDivOpacity() {
   Util.logMsg('Toggling view div opacity.');
   oldViewDiv.style.visibility = "hidden";
   MainMap.activeViewDiv.style.opacity = "1";
}

//Double the number of basemap tile levels of detail.
function addNewLODs() {
   let i: number;
   let level = -1;
   let newLOD: __esri.LOD;
   const newLODs: __esri.LOD[] = [];
   const curLODs = MainMap.mapView.constraints.effectiveLODs;

   if (!curLODs) {
      return; //Quit if basemap is an imageryLayer.
   }

   for (i = 0; i < curLODs.length - 1; i++) {
      const curLOD = curLODs[i];
      const nextLOD = curLODs[i + 1];

      //Add the current LOD into the new list of LODs.
      newLOD = JSON.parse(JSON.stringify(curLOD)) as __esri.LOD; //Clone it.
      level++;
      newLOD.level = level;
      newLODs.Add(newLOD);

      //Create a new LOD halfway between the current LODs.
      newLOD = JSON.parse(JSON.stringify(curLOD)) as __esri.LOD; //Clone it.
      level++;
      newLOD.level = level;
      newLOD.resolution = (curLOD.resolution + nextLOD.resolution) / 2.05; //Increase resolution to use best level.
      newLOD.scale = (curLOD.scale + nextLOD.scale) / 2.05;
      newLODs.Add(newLOD);
   }

   //Flush out the last one.
   newLOD = JSON.parse(JSON.stringify(curLODs[curLODs.length - 1])) as __esri.LOD; //Clone it.
   level++;
   newLOD.level = level;
   newLODs.Add(newLOD);

   //Add large scale (zoomed-in) levels.
   //for (i = 0; i < 2; i++) {
   //   const nextRes: number = newLOD.resolution / 2;
   //   const nextScale: number = newLOD.scale / 2;

   //   const middleRes = (newLOD.resolution + nextRes) / 2;
   //   const middleScale = (newLOD.scale + nextScale) / 2;

   //   newLOD = JSON.parse(JSON.stringify(newLOD)) as __esri.LOD; //Clone it.
   //   level++;
   //   newLOD.level = level;
   //   newLOD.resolution = middleRes;
   //   newLOD.scale = middleScale;
   //   newLODs.Add(newLOD);

   //   newLOD = JSON.parse(JSON.stringify(newLOD)) as __esri.LOD; //Clone it.
   //   level++;
   //   newLOD.level = level;
   //   newLOD.resolution = nextRes;
   //   newLOD.scale = nextScale;
   //   newLODs.Add(newLOD);
   //}

   //Update the map views zoom levels.
   MainMap.mapView.constraints.lods = newLODs;
   //MainMap.mapView.constraints.minScale = MainMap.mapView.constraints.effectiveMinScale;
   //MainMap.mapView.constraints.minZoom = 0;
   //MainMap.mapView.constraints.maxZoom = level;

   //Util.errMsg("Original LODs: ", curLODs);
   //Util.errMsg("New LODs: ", newLODs);
   //Util.errMsg("Constraints: ", MainMap.mapView.constraints);
}

//Initialize map exents.
function initMapExtents() {
   Util.logMsg('Initializing map extents');

   mapExtentStart = getMapExtent(Settings.mapExtentStart, Settings.wkID);
   mapExtentFull = getMapExtent(Settings.mapExtentFull, Settings.wkID);

   //Get map extent query string.
   const mapExtentQS = getMapExtent(QueryString.mapExtent, Settings.wkID);
   if (mapExtentQS) {
      mapExtentStart = mapExtentQS;
   }
}

//Get map extent from comma delimmited string.
export function getMapExtent(str: string, wkid?: number): __esri.Extent {
   let mapExtent: __esri.Extent = null;

   if (str) {
      const sa: string[] = str.split(",");

      if (sa.length === 4 && wkid) {
         mapExtent = new Extent({
            xmin: Util.parseFlt(sa[0]),
            ymin: Util.parseFlt(sa[1]),
            xmax: Util.parseFlt(sa[2]),
            ymax: Util.parseFlt(sa[3]),
            spatialReference: { wkid: wkid },
         });

         //WKID is the last argument.
      } else if (sa.length === 5) {
         mapExtent = new Extent({
            xmin: Util.parseFlt(sa[0]),
            ymin: Util.parseFlt(sa[1]),
            xmax: Util.parseFlt(sa[2]),
            ymax: Util.parseFlt(sa[3]),
            spatialReference: { wkid: Util.parseInteger(sa[4]) },
         });
      }
   }
   return mapExtent;
}

function showBusy() {
   busy = true;
   if (Spinner) {
      Spinner.show();
   }
}

function hideBusy() {
   if (Spinner) {
      Spinner.hide();
   }
   busy = false;
}
